/**
 * useTableroView — the browser view (verdict, motivo, search, page) read from
 * and written to the URL (issue #336; rules in urlState.ts). The KPI cards and
 * the expediente browser both use it, so they always agree.
 */

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { REASON_OPTIONS } from "../../components/decisionChips";
import {
  applyViewChange,
  parseTableroView,
  serializeTableroView,
  type TableroView,
} from "./urlState";

const REASON_CODES = REASON_OPTIONS.map((o) => o.code);

export function useTableroView(): {
  view: TableroView;
  /** Change part of the view. `replace` (used while typing the search) avoids
   * one history entry per keystroke. */
  update: (patch: Partial<TableroView>, options?: { replace?: boolean }) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = useMemo(
    () => parseTableroView(searchParams, REASON_CODES),
    [searchParams],
  );
  const update = useCallback(
    (patch: Partial<TableroView>, options?: { replace?: boolean }) => {
      setSearchParams(
        (prev) =>
          serializeTableroView(
            applyViewChange(parseTableroView(prev, REASON_CODES), patch),
            prev,
          ),
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );
  return { view, update };
}
